import BlogPage from 'src/views/Marketing/Blog/components/BlogPage';
import Footer from 'src/views/Marketing/components/Footer';
import NavBar from 'src/views/Marketing/components/NavBar';
import { ScrollView, StyleSheet } from 'react-native';

export default function Page() {
  return (
    <ScrollView style={styles.scrollView}>
      <NavBar />
      <BlogPage author='OneShop Team' dateTime='2022-01-22' tag='eBay' title='The eBay Seller Guide: Listing 101'>
        <p>
          When it comes to online marketplaces, eBay is one of the pioneers, having had its first sale as early as the
          mid-90s. However, it was first known for its auction-style listings with mostly collectibles and one-offs
          bids. Nowadays, though, you can find almost anything on this marketplace with a fixed price, just like on
          Amazon, Shopify, or any other e-commerce platform.{' '}
        </p>
        <p>
          Before you gather your goods and start your eBay listing journey, first things first. Decide if you're setting
          up a personal or a business account. If you're looking to clear out your closet before the next season and
          perhaps to occasionally sell some items here and there throughout the year, a personal account would be a
          better fit. Otherwise, get a business account if you plan to replenish your inventory regularly with plenty to
          list at any given time.{' '}
        </p>
        <p>
          Second, for every item you're listing, decide if you're going to set a starting price (Yes, auction-style
          option, though rare to see these days, is still available) or assign a "Buy it Now" fixed price right off the
          bat.{' '}
        </p>
        <p>And now, to the listing anatomy. Here's a to-do list to help in preparing your listing. </p>
        <p>
          <strong>Choose a listing tool.</strong> What's good about eBay is its selection of free listing tools. Again,
          depending on how frequent you intend to list and the volume of your inventory, choose the right listing tool
          for you. <strong>Quick Listing Tool</strong> has all the basic fields you need to fill out or choose with the
          drop-down button, such as the price and shipping options. If you need to add more fields, such as to set the
          duration of the auction or to set a reserve price, you can easily edit the preferences on your seller
          dashboard to include this information. <strong>Business Tool</strong> and <strong>Seller Hub</strong> are for
          the more committed sellers who regularly list in bulk and need more extensive fields.{' '}
          <strong>New Listing Tool</strong> is the improved version of the Quick Listing Tool with a more streamlined
          interface and better listing features.{' '}
        </p>
        <p>
          <strong>Describe the item.</strong> Craft clear sentences (no typos and misspellings, please) that include
          essential, factual information that would make a buyer decide to purchase your item. Incorporate item
          specifics such as the brand and style name, color, size, and material. Aside from the brand and type or style
          name, which are typically keywords shoppers use to search, don't worry about eBay SEO as it works differently
          from Google SEO. In eBay, seller performance metrics impact the search rankings of listings more than keyword
          density.{' '}
        </p>
        <p>
          <strong>Disclose its condition.</strong> The options for disclosing the item condition will appear according
          to the item you are listing. Potential buyers of certain items (e.g., gadgets, appliances, pieces of
          furniture) usually need more specific information on the item's condition beyond "New" or "Used." They will
          want to know if it's "New with tags/box" or "Refurbished" in Excellent, Very Good, or Good condition with its
          original or new accessories. Meanwhile, potential buyers of vintage or collector's items would want to know if
          the listing is partially or fully functional or entirely a decorative piece only. Regardless of the item in
          question, the rule of thumb is, to be honest to avoid any dispute later on.{' '}
        </p>
        <p>
          <strong>Include any product identifiers.</strong> Certain items have industry-specific codes assigned to them
          for accurate identification. Include them whenever available. For instance, if you're selling books, including
          the International Standard Book Number (ISBN) and the Manufacturer Part Number (MPN) for all sorts of
          electronic and automotive parts.{' '}
        </p>
        <p>
          <strong>Provide high-quality product photos.</strong> To protect your seller's performance and reputation,
          ensure compliance with the eBay guidelines at all times, and that includes not using stolen images for your
          product photos. Each listing usually has allotted space for 12 photos. Make sure you take sharp, clear photos
          that depict your item accurately and pleasantly. Ensure, too, that the size is within the allowed dimensions –
          not less than 500 x 500 pixels and not more than 9,000 x 9,000 pixels.{' '}
        </p>
        <p>
          <strong>Set your payment and shipping options.</strong> Identify the payment methods (e.g., Paypal, debit or
          credit cards, Google Pay, Apple Pay) and shipping options you will be offering your potential buyers. You may
          offer free shipping. Just make sure you won't be at a loss after total fees have been applied to the item
          sold.{' '}
        </p>
        <p>
          <strong>Determine your return policy.</strong> Many shoppers take a store's return policy seriously and may
          make or break a sale. Take this into serious consideration, too, in crafting your return policy. Be clear on
          whether or not you accept returns, how long you'd accept returns, and who must shoulder the fees of shipping
          the item back.{' '}
        </p>
        <p>
          <strong>Know your selling limits.</strong> eBay is big on seller performance. All new sellers – who are still
          getting the hang of selling on the platform – have a default selling limit of 10 items (worth a total of $500)
          per month until such time they have gained impressive sales metrics or availed of store packages. Make it a
          habit to check your seller dashboard to see how many more items you can still list. Request for increase in
          selling limit may be approved on a case-to-case basis.{' '}
        </p>

        <h3>The Fees</h3>
        <p>
          Selling on eBay is more complicated than its counterparts, with more straightforward fee policies. Several
          factors impact different kinds of fees. These are the two important fees to keep in mind for beginners:
          Insertion Fee and Final Value Fee.{' '}
        </p>
        <h4>Upon listing the item: Insertion Fee</h4>
        <p>
          The Insertion Fee is $0.35 per item for most categories. This fee is usually waived for the first 200 - 250
          listings that you make in a month, depending on your eBay Store subscription level. To encourage the use of
          the eBay-managed payment option, eBay offers additional "zero insertion fee" listings for accounts that let
          eBay manage their payments.{' '}
        </p>
        <p>
          Your "zero insertion fee" count decreases every time you make a new listing, relist the item, and if you list
          the same item in two or more categories, and whenever a "Good Til Cancelled" listing gets automatically
          renewed monthly. A listing with multiple items is counted only as one. Also, take note that this waived
          insertion fee doesn't apply to a few categories such as real estate and some of those about motors, vehicles,
          and industrial parts and equipment.{' '}
        </p>
        <p>
          If you opted for any add-ons and upgrades, such as options for promotion to boost your item's visibility and
          clicks to your listing. As mentioned earlier, eBay values seller performance. Add to this the excellent
          customer service you provide, so if you're not keen on paying extra for advertising, favorable feedback from
          customers or guests would work just as well and may get you a high ranking on eBay search results.{' '}
        </p>
        <h4>Upon sale of the item: Final Value Fee</h4>
        <p>
          Once an item gets sold, the Final Value Fee will be computed based on the category of your listing and its
          total amount of sale (the item's price plus shipping and other applicable charges).{' '}
        </p>
        <p>
          The percentage is taken off the total amount, which typically ranges from 12.3% to 15% per item. More
          expensive items are subjected to an additional 2.3% to 9% on the sale portion exceeding a certain amount. For
          example, in most categories (including vinyl records and automotive tools and supplies), 12.55% is taken from
          the total amount of the sale worth up to $7,500 per item. Then an additional 2.35% is deducted from the
          portion of the sale exceeding $7,500.{' '}
        </p>
        <p>
          Please take note that $0.30 is also deducted per order, which eBay defines as "any number of items purchased
          by the same buyer at checkout with the same shipping method." If you need to cancel a sale and refund the
          buyer, a portion of the Final Value Fee may be refunded to you, but not the $0.30 charge per order.{' '}
        </p>
        <p>
          Take note of all these basic fees when pricing your listing to make sure you're pocketing a nice profit margin
          for each successful transaction you have on eBay.{' '}
        </p>
        <p>
          Listing and selling on eBay may take some getting used to. The platform knows this, too. That's why they have
          restrictive seller limits for beginners to ensure they don't list too many items too soon without gaining
          enough experience and mastery of its ins and outs. Start listing a few items and see if eBay is for you!
        </p>
      </BlogPage>
      <Footer />
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
  },
});
